import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import apiFeedbacks, { IFeedback } from 'api/feedbacks';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';

import { FEEDBACK_MOODS } from './FeedbackButton';

import 'style/feedback/Feedback.css';

// ProBid: the offers; the notifications are looked for when it is opened
const PROBID_PATH = '/offers';

const emojiOf = (mood: string) =>
  FEEDBACK_MOODS.find((choice) => choice.mood === mood)?.emoji ?? mood;

/**
 * A floating window that tells the user what their feedback has led to: when they open
 * ProBid, the responses the development has told them of are shown one at a time, with
 * their own feedback. The user has not tried the change yet, so they are asked to try it
 * and give new feedback the usual way. The window closes only by acknowledging it, so it is
 * recorded when the user has read the response.
 */
const FeedbackNotification = (): JSX.Element | null => {
  const { t, i18n } = useTranslation([translations.common]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.common}:feedback.${key}`, options);
  const location = useLocation();

  const [notifications, setNotifications] = useState<IFeedback[] | null>(null);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  // Looked for each time ProBid is opened, also without reloading the application, so a
  // response told of while the application is open shows when the user comes to ProBid
  const inProBid = location.pathname.startsWith(PROBID_PATH);
  useEffect(() => {
    if (!inProBid) {
      return undefined;
    }
    let cancelled = false;
    apiFeedbacks
      .getNotifications()
      .then((result) => !cancelled && setNotifications(result))
      .catch(() => !cancelled && setNotifications([]));
    return () => {
      cancelled = true;
    };
  }, [inProBid]);

  const current = notifications?.[0];
  if (!current) {
    return null;
  }

  const acknowledge = async () => {
    setSending(true);
    setFailed(false);
    try {
      await apiFeedbacks.closeNotification(current.id);
      setNotifications((shown) => (shown ?? []).slice(1));
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  const date = (value: null | string | undefined) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : '';

  return (
    <aside
      aria-labelledby="feedback-notification-title"
      className="feedback-notification"
      role="dialog"
    >
      {/* No close button: the window closes when the user has acknowledged it */}
      <header>
        <h2 id="feedback-notification-title">
          <span aria-hidden="true">📣</span> {tr('Notification title')}
        </h2>
      </header>

      <div className="feedback-notification-body">
        {/* The user's own feedback */}
        <p className="feedback-notification-label">
          {tr('Your feedback', { date: date(current.createdAt) })}
        </p>
        <p className="feedback-notification-page">
          {current.location
            ? `${current.page} › ${current.location}`
            : current.page}
        </p>
        <blockquote className="feedback-notification-quote">
          <span aria-label={tr(`moods.${current.mood}`)}>
            {emojiOf(current.mood)}
          </span>
          {current.rating === null ? null : (
            <span className="feedback-notification-rating">
              {`${current.rating} / 10`}
            </span>
          )}
          <span>{current.text || '–'}</span>
        </blockquote>

        {/* What it has led to */}
        <p className="feedback-notification-label">
          {tr('Response by', {
            date: date(current.respondedAt),
            name: current.respondedUserName ?? '',
          })}
          {current.status ? (
            <span className={`feedback-status chosen ${current.status}`}>
              {tr(`statuses.${current.status}`)}
            </span>
          ) : null}
        </p>
        <p className="feedback-notification-response">{current.response}</p>

        {/* The user has not tried it yet: try it and give new feedback the usual way */}
        <p className="feedback-notification-try">
          <span aria-hidden="true">👉</span> {tr('Try again')}
        </p>
        {failed ? <p className="feedback-failed">{tr('Failed')}</p> : null}
      </div>

      <footer>
        {notifications && notifications.length > 1 ? (
          <span className="feedback-notification-more">
            {tr('More notifications', { count: notifications.length - 1 })}
          </span>
        ) : null}
        <PrimaryButton disabled={sending} onClick={acknowledge}>
          {tr('Acknowledge')}
        </PrimaryButton>
      </footer>
    </aside>
  );
};

export default FeedbackNotification;
